import * as React from 'react';
import { createPortal } from 'react-dom';
import { XIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils/cn';
import { useAnimatedVisibility, useBodyScrollLock } from '@/lib/hooks';
import { usePanelStackStore } from '@/store/panel-stack-store';

interface SlideoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  side?: 'left' | 'right';
  className?: string;
  /** Nesting level for stacked panels (0 = base, 1 = first nested, etc.) */
  level?: number;
  /** Override default z-index */
  style?: React.CSSProperties;
  /** Disable body scroll lock (for nested panels that rely on parent's lock) */
  disableBodyLock?: boolean;
  /** Disable backdrop (useful for nested panels) */
  disableBackdrop?: boolean;
  /** Form panels appear above global detail panels (z-85) but below dialogs (z-90) */
  isFormPanel?: boolean;
}

interface SlideoverContentProps {
  children: React.ReactNode;
  className?: string;
}

interface SlideoverHeaderProps {
  children: React.ReactNode;
  className?: string;
}

interface SlideoverTitleProps {
  children: React.ReactNode;
  className?: string;
}

interface SlideoverDescriptionProps {
  children: React.ReactNode;
  className?: string;
}

interface SlideoverFooterProps {
  children: React.ReactNode;
  className?: string;
}

const Slideover = ({
  open,
  onOpenChange,
  children,
  side = 'right',
  className,
  level,
  style,
  disableBodyLock = false,
  disableBackdrop = false,
  isFormPanel = false,
}: SlideoverProps) => {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = React.useState(false);

  // Use animated visibility hook for enter/exit animations
  // Pass domReady: mounted to prevent animation skip on initial page load
  const { shouldRender, isVisible } = useAnimatedVisibility(open, {
    duration: 300,
    domReady: mounted,
  });

  // Ensure portal is only created on client
  React.useEffect(() => {
    setMounted(true);
  }, []);

  // Reference-counted body scroll lock (safe with nested slideovers/dialogs)
  useBodyScrollLock(shouldRender && !disableBodyLock);

  // Calculate z-index based on level: base 50 + (level * 10)
  // Form panels auto-detect when global panels are open and use z-85
  // to appear above all global detail panels but below dialogs (z-90)
  const zIndex = React.useMemo(() => {
    if (isFormPanel) return 85; // Explicit form panel override
    if (level !== undefined) return 50 + level * 10; // Global detail panels use level-based z-index
    // No level passed (form panels): auto-detect if global panels are open
    const hasGlobalPanels = usePanelStackStore.getState().stack.length > 0;
    return hasGlobalPanels ? 85 : 50;
  }, [isFormPanel, level]);

  // Calculate width based on level: starts at 87vw, decreases by 3vw per level
  const baseWidth = 87;
  const effectiveLevel = level ?? 0;
  const widthReduction = effectiveLevel * 3;
  const calculatedWidth = Math.max(65, baseWidth - widthReduction);

  // Set CSS variable for slideover width (used by toast positioning)
  React.useEffect(() => {
    if (shouldRender && contentRef.current) {
      const updateWidth = () => {
        if (contentRef.current) {
          const width = contentRef.current.getBoundingClientRect().width;
          document.documentElement.style.setProperty('--slideover-width', `${width}px`);
        }
      };
      // Initial measurement after render
      updateWidth();
      // Watch for size changes
      const resizeObserver = new ResizeObserver(updateWidth);
      resizeObserver.observe(contentRef.current);
      return () => {
        resizeObserver.disconnect();
        document.documentElement.style.removeProperty('--slideover-width');
      };
    } else {
      document.documentElement.style.removeProperty('--slideover-width');
    }
  }, [shouldRender]);

  if (!shouldRender || !mounted) return null;

  // Compute shadow intensity based on level (deeper = stronger shadow)
  const shadowIntensity = Math.min(0.15 + (level ?? 0) * 0.05, 0.3);
  const shadowStyle = `0 4px 6px -1px rgba(0,0,0,${shadowIntensity * 0.5}), 0 10px 20px -5px rgba(0,0,0,${shadowIntensity}), 0 25px 50px -12px rgba(0,0,0,${shadowIntensity * 1.2})`;

  const slideoverContent = (
    <div
      className="fixed inset-0"
      style={{ zIndex, ...style }}
      onClick={(e) => {
        // Close when clicking on the backdrop or the outer container
        if (
          e.target === e.currentTarget ||
          (e.target as HTMLElement).classList.contains('slideover-backdrop')
        ) {
          onOpenChange(false);
        }
      }}
    >
      {/* Backdrop with fade animation - only for level 0 unless disabled */}
      {!disableBackdrop && (
        <div
          className={cn(
            'slideover-backdrop fixed inset-0',
            (level ?? 0) === 0 ? 'bg-black/20' : 'bg-black/5',
            'transition-opacity duration-300 ease-in-out',
            isVisible ? 'opacity-100' : 'opacity-0',
          )}
          onClick={() => onOpenChange(false)}
          aria-hidden="true"
        />
      )}
      {/* Content with slide animation */}
      <div
        ref={contentRef}
        className={cn(
          'fixed top-0 bottom-0 z-10 bg-background',
          side === 'right' ? 'border-l' : 'border-r',
          'border-border/80',
          'transition-transform duration-300 ease-in-out',
          // Slide animation based on side and visibility
          side === 'right'
            ? isVisible
              ? 'translate-x-0'
              : 'translate-x-full'
            : isVisible
              ? 'translate-x-0'
              : '-translate-x-full',
          // Only apply className if provided, otherwise use default sizing
          className,
        )}
        style={{
          // Apply level-based positioning and sizing if no className override
          ...(className
            ? {}
            : {
                width: `calc(${calculatedWidth}vw / var(--zoom-factor, 1))`,
              }),
          right: side === 'right' ? 0 : undefined,
          left: side === 'left' ? 0 : undefined,
          boxShadow: shadowStyle,
        }}
        onClick={(e) => {
          // Prevent clicks inside the slideover content from closing it
          e.stopPropagation();
        }}
      >
        {children}
      </div>
    </div>
  );

  // Render through portal to document.body for proper stacking
  return createPortal(slideoverContent, document.body);
};

const SlideoverContent = React.forwardRef<HTMLDivElement, SlideoverContentProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('flex flex-col h-full', className)} {...props}>
        {children}
      </div>
    );
  },
);
SlideoverContent.displayName = 'SlideoverContent';

const SlideoverHeader = React.forwardRef<HTMLDivElement, SlideoverHeaderProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'relative flex flex-col gap-1.5 px-6 pt-6 pb-5 flex-shrink-0',
          'bg-gradient-to-b from-primary/[0.03] to-transparent',
          className,
        )}
        {...props}
      >
        {/* Decorative accent line */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-primary via-primary/80 to-primary/40" />
        {children}
      </div>
    );
  },
);
SlideoverHeader.displayName = 'SlideoverHeader';

const SlideoverTitle = React.forwardRef<HTMLHeadingElement, SlideoverTitleProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <h2
        ref={ref}
        className={cn('text-lg font-semibold leading-6 text-foreground tracking-tight', className)}
        {...props}
      >
        {children}
      </h2>
    );
  },
);
SlideoverTitle.displayName = 'SlideoverTitle';

const SlideoverDescription = React.forwardRef<HTMLParagraphElement, SlideoverDescriptionProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <p
        ref={ref}
        className={cn('text-sm text-muted-foreground leading-relaxed', className)}
        {...props}
      >
        {children}
      </p>
    );
  },
);
SlideoverDescription.displayName = 'SlideoverDescription';

const SlideoverFooter = React.forwardRef<HTMLDivElement, SlideoverFooterProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center gap-2 px-6 py-4 flex-shrink-0',
          'bg-muted/30 dark:bg-muted/10',
          'border-t border-border/50',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  },
);
SlideoverFooter.displayName = 'SlideoverFooter';

interface SlideoverCloseProps {
  onClose: () => void;
  className?: string;
}

const SlideoverClose = ({ onClose, className }: SlideoverCloseProps) => {
  return (
    <button
      type="button"
      onClick={onClose}
      className={cn(
        'absolute right-4 top-4 z-10 p-1.5 rounded-lg',
        'text-muted-foreground/60 hover:text-foreground',
        'bg-transparent hover:bg-muted/80',
        'transition-all duration-150 ease-out',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20',
        className,
      )}
      aria-label="Close"
    >
      <XIcon className="w-3.5 h-3.5" />
      <span className="sr-only">Close</span>
    </button>
  );
};

export {
  Slideover,
  SlideoverContent,
  SlideoverHeader,
  SlideoverTitle,
  SlideoverDescription,
  SlideoverFooter,
  SlideoverClose,
};
